import { isWallDisplayOnly } from "../../auth/roles";
import { isRelease150 } from "../../lib/release150";
import { ownerOf } from "../hub/hubs";
import type { GoToItem } from "../header/goToModel";
import type { PinnedTile, SavedFilter } from "../../api/homeApi";

// Release 1.5.0 Home (O-R64–O-R66), the pure part: which screens keep saved filters, how a page becomes a tile or a start
// page, and how tiles move. The server repeats every check; this only decides what to show.

export interface FilterScreen {
  key: string;
  label: string;
  path: string;
  params: readonly string[];
}

/** O-R66: the list screens whose filters live in the address bar, with the parameters each owns (same list as the API). */
export const SAVED_FILTER_SCREENS: readonly FilterScreen[] = [
  { key: "work-orders", label: "Work orders", path: "/operations/work-orders", params: ["part", "partMode", "customer", "productLine", "subsetPrefix", "keyLevel", "health", "dueWindow"] },
  { key: "ready-for-operations", label: "Ready for Operations", path: "/operations/ready", params: ["view", "q", "customer", "project", "shop", "keyLevel", "stage", "engineering", "programming", "material", "everyGateOnRouter", "openGate", "nextWorkcenter", "nextOpType", "released", "onHold", "dueFrom", "dueTo", "dueWindow", "readySinceFrom", "readySinceTo", "minDaysReady", "maxDaysReady", "sortBy", "sortDirection"] },
  { key: "hotlist", label: "Hotlist & holds", path: "/shop-floor/hotlist", params: ["priority", "q"] },
  { key: "dispatch", label: "Dispatch lists", path: "/shop-floor/dispatch", params: ["shop", "wc", "state", "q", "rule"] },
  { key: "order-gantt", label: "Order Gantt", path: "/planning/orders", params: ["sort"] },
  { key: "resource-gantt", label: "Machine Gantt", path: "/planning/resources", params: ["sort"] },
  // Release 1.6.0 (O-R72): the Planning Calendar's view and machine filters (never the date, so it opens on today).
  { key: "planning-calendar", label: "Planning calendar", path: "/planning/calendar", params: ["view", "type", "shop", "wc", "plan", "changes"] },
  { key: "purchasing-queue", label: "Purchasing queue", path: "/purchasing", params: ["status", "vendor", "shop", "q"] },
  { key: "shipping", label: "Shipping", path: "/shop-floor/shipping", params: ["customer", "horizon", "q", "section", "day", "readiness", "cause"] },
  { key: "purchasing-view", label: "Purchasing", path: "/shop-floor/purchasing", params: ["vendor", "shop", "q", "section"] },
  { key: "working-now", label: "Who's working", path: "/shop-floor/working-now", params: ["shop", "wc", "q"] },
  { key: "kanban", label: "Shop Kanban", path: "/shop-floor/kanban", params: ["q", "priority", "wc", "lane"] },
  { key: "material", label: "Material & OSP", path: "/shop-floor/material", params: ["section", "q", "launch", "material", "osp", "vendor", "lookAhead", "band", "needBand"] },
  { key: "parked", label: "Parked work orders", path: "/shop-floor/parked", params: ["part", "customer", "shop", "range"] },
  { key: "osp-board", label: "OSP board", path: "/shop-floor/osp", params: ["vendor", "customer", "shop", "q", "view"] },
  { key: "capacity", label: "Capacity & machines", path: "/shop-floor/capacity", params: ["shop", "q"] },
  { key: "customers", label: "Customers", path: "/customers", params: ["search", "risk", "followups", "sort"] }
];

export const HOME_PATH = "/home";
export const ROLE_START_PAGES_PATH = "/admin/start-pages";

function trimPath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

export function screenForPath(pathname: string): FilterScreen | null {
  const p = trimPath(pathname);
  return SAVED_FILTER_SCREENS.find((s) => s.path === p) ?? null;
}

/** The screen's own filter parameters from a query string, in the screen's order, empty values dropped (O-R66). */
export function filterQueryFor(screen: FilterScreen, search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const out: string[] = [];
  for (const name of screen.params) {
    for (const value of params.getAll(name)) {
      // Escaped as the server stores it (RFC 3986), so a saved filter and the address compare equal.
      if (value) out.push(`${name}=${encodeURIComponent(value).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)}`);
    }
  }
  return out.join("&");
}

/** Two queries name the same filters (order and encoding aside). */
export function sameFilters(screen: FilterScreen, a: string, b: string): boolean {
  return filterQueryFor(screen, a) === filterQueryFor(screen, b);
}

/** The page as a start page or tile target: path plus, on a filter screen, only its filter parameters. */
export function pageTarget(pathname: string, search: string): string {
  const path = trimPath(pathname);
  const screen = screenForPath(path);
  const query = screen ? filterQueryFor(screen, search) : "";
  return query ? `${path}?${query}` : path;
}

/** Pages where the page tools (Pin, Make start page, Saved filters) are not offered. */
const NO_TOOLS = ["/", HOME_PATH, "/sign-in", "/verify", "/display", "/request-access", "/not-authorized"];

export function offersPageTools(pathname: string): boolean {
  const p = trimPath(pathname);
  if (NO_TOOLS.includes(p)) return false;
  if (/^\/purchasing\/requests\/[^/]+\/router\/?$/.test(p)) return false;
  return true;
}

/** A work order opened at /work-orders/{job|suffix}: its key, or null. */
export function workOrderKeyFromPath(pathname: string): string | null {
  const m = /^\/work-orders\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  let key: string;
  try {
    key = decodeURIComponent(m[1]!).trim();
  } catch {
    return null;
  }
  return /^[A-Za-z0-9]{1,10}\|[A-Za-z0-9]{0,4}$/.test(key) ? key.toUpperCase() : null;
}

/** "Work order 600100-000" — the number only, never part or customer data (O-R65). */
export function workOrderLabel(key: string): string {
  const [job, suffix] = key.split("|");
  return suffix ? `Work order ${job}-${suffix}` : `Work order ${job}`;
}

/** The name a page gets as a tile or a start page: its menu label, or the work order's number. */
export function pageLabelFor(pathname: string): string {
  const key = workOrderKeyFromPath(pathname);
  if (key) return workOrderLabel(key);
  const p = trimPath(pathname);
  if (p === ROLE_START_PAGES_PATH) return "Role start pages";
  const owner = ownerOf(p);
  if (owner) return owner.item.to === p ? owner.item.label : `${owner.item.label} (detail)`;
  return "This page";
}

/** The tile request for the page the person is on: a work order, or a page with its filters. */
export function tileForPage(pathname: string, search: string): { kind: "page" | "work-order"; target: string; label: string } {
  const key = workOrderKeyFromPath(pathname);
  if (key) return { kind: "work-order", target: key, label: workOrderLabel(key) };
  const target = pageTarget(pathname, search);
  const screen = screenForPath(pathname);
  return { kind: "page", target, label: screen && target.includes("?") ? `${screen.label} (filtered)` : pageLabelFor(pathname) };
}

/** The pinned tile matching this page, if any. */
export function findPinned(tiles: readonly PinnedTile[] | undefined, kind: string, target: string): PinnedTile | null {
  return tiles?.find((t) => t.kind === kind && t.target === target) ?? null;
}

export type KpiSource = "hub" | "hotlist" | "dispatch" | "purchasing-queue";

/** The KPI tile target: `source/id` or `dispatch/id/SHOP` (O-R65). */
export function kpiTarget(source: KpiSource, kpiId: string, scope?: string | null): string {
  return scope ? `${source}/${kpiId}/${scope}` : `${source}/${kpiId}`;
}

export function parseKpiTarget(target: string): { source: KpiSource; kpiId: string; scope: string | null } | null {
  const parts = target.split("/");
  if (parts.length < 2 || parts.length > 3) return null;
  const source = parts[0] as KpiSource;
  if (!["hub", "hotlist", "dispatch", "purchasing-queue"].includes(source)) return null;
  return { source, kpiId: parts[1]!, scope: parts[2] ?? null };
}

/** Moves one tile by `delta` places (keyboard "Move earlier/later"); unchanged at either end. */
export function moveBy(ids: readonly number[], id: number, delta: number): number[] {
  const from = ids.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return [...ids];
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

/** Drag and drop: the dragged tile takes the place of the tile it was dropped on. */
export function moveTo(ids: readonly number[], dragged: number, target: number): number[] {
  const to = ids.indexOf(target);
  if (to < 0 || dragged === target || !ids.includes(dragged)) return [...ids];
  const next = ids.filter((i) => i !== dragged);
  next.splice(to, 0, dragged);
  return next;
}

/** The person's default filter for the screen, or null. */
export function defaultFilter(filters: readonly SavedFilter[] | undefined, screenKey: string): SavedFilter | null {
  return filters?.find((f) => f.screen === screenKey && f.isDefault) ?? null;
}

/** "Go to" entries for Home (header-1-5's registry): Home for everyone, the role table for Admins. Nothing when 1.5.0 is off. */
export function homeGoToItems(roles: readonly string[]): GoToItem[] {
  if (!isRelease150() || roles.length === 0 || isWallDisplayOnly(roles)) return [];
  const items: GoToItem[] = [
    { id: "page:home", label: "Home", group: "Pages", detail: "Your start page, pinned tiles and saved filters", to: HOME_PATH, keywords: "home start page pinned tiles saved filters" }
  ];
  if (roles.includes("Admin")) {
    items.push({ id: "page:role-start-pages", label: "Role start pages", group: "Admin", detail: "Which start view each role sees on Home", to: ROLE_START_PAGES_PATH, keywords: "role start pages home view admin" });
  }
  return items;
}


